import { useState, useEffect } from 'react';
import { RefreshCw, Wifi, WifiOff, QrCode, Phone, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const WhatsAppQRConfiguration = ({ channel, isEditing, editData, setEditData }) => {
  const [qrStatus, setQrStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (channel.instance_id) {
      loadQRStatus();
    }
  }, [channel.instance_id]);

  const loadQRStatus = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/channels/${channel.id}/whatsapp-qr/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const statusData = await response.json();
        setQrStatus(statusData);
        
        // If not connected, try to get QR code
        if (!statusData.connected) {
          await loadQRCode();
        }
      }
    } catch (error) {
      console.error('Error loading QR status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQRCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/channels/${channel.id}/whatsapp-qr/qr-code`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const qrData = await response.json();
        if (qrData.qr) {
          setQrCode(qrData.qr);
        }
      }
    } catch (error) {
      console.error('Error loading QR code:', error);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) return;
    
    try {
      setDisconnecting(true);
      
      const response = await fetch(`${API_BASE_URL}/channels/${channel.id}/whatsapp-qr/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        await loadQRStatus(); // Refresh status
        alert('WhatsApp desconectado com sucesso');
      } else {
        alert('Erro ao desconectar');
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert('Erro ao desconectar');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!channel.instance_id) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Instância Não Configurada
        </h3>
        <p className="text-gray-600 mb-6">
          Este canal precisa de uma instância WhatsApp para funcionar.
        </p>
        <button
          onClick={() => window.location.href = `/channels/${channel.id}/configure`}
          className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all"
        >
          <ExternalLink className="w-5 h-5" />
          Configurar Instância
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status da Conexão */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Status da Conexão WhatsApp</h3>
        <button
          onClick={loadQRStatus}
          disabled={loading}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
          title="Atualizar status"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Verificando status...</p>
        </div>
      ) : qrStatus?.connected ? (
        // Conectado
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Wifi className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-green-900">WhatsApp Conectado</h4>
                <p className="text-green-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {qrStatus.number || 'Número conectado'}
                </p>
              </div>
            </div>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              {disconnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <WifiOff className="w-4 h-4" />
              )}
              {disconnecting ? 'Desconectando...' : 'Desconectar'}
            </button>
          </div>
        </div>
      ) : (
        // Desconectado - Mostrar QR Code
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <WifiOff className="w-5 h-5 text-amber-600" />
              <div>
                <h4 className="font-medium text-amber-900">WhatsApp Desconectado</h4>
                <p className="text-amber-700 text-sm">
                  Escaneie o QR Code abaixo para conectar
                </p>
              </div>
            </div>
          </div>

          {qrCode ? (
            <div className="text-center">
              <div className="inline-block p-4 bg-white border-2 border-gray-200 rounded-2xl shadow-sm">
                <img 
                  src={qrCode} 
                  alt="QR Code WhatsApp" 
                  className="w-48 h-48 mx-auto"
                />
              </div>
              <div className="flex items-center justify-center gap-2 text-gray-600 mt-2">
                <QrCode className="w-4 h-4" />
                <span className="text-sm">Escaneie com seu WhatsApp</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-6 h-6 text-gray-500" />
              </div>
              <p className="text-gray-600">
                QR Code não disponível. 
                <button 
                  onClick={loadQRCode}
                  className="text-green-600 hover:text-green-700 ml-1"
                >
                  Tentar novamente
                </button>
              </p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h4 className="font-medium text-blue-900 mb-2">Como conectar:</h4>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Abra o WhatsApp no seu celular</li>
              <li>Vá em Configurações → Aparelhos conectados</li>
              <li>Toque em "Conectar um aparelho"</li>
              <li>Escaneie o QR Code acima</li>
            </ol>
          </div>
        </div>
      )}

      {/* Informações da Instância */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="font-medium text-gray-900 mb-2">Informações da Instância</h4>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex justify-between">
            <span>ID da Instância:</span>
            <code className="text-xs bg-gray-200 px-2 py-1 rounded">
              {channel.instance_id}
            </code>
          </div>
          <div className="flex justify-between">
            <span>Status da Instância:</span>
            <span className={`px-2 py-1 rounded text-xs ${
              qrStatus?.status === 'connected' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {qrStatus?.status || 'Verificando...'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppQRConfiguration;